import {
  CardStyles,
  IconStyles,
  BorderRadius,
  ShadowSize,
  BorderWidth,
  IconShape,
  IconSize,
} from 'server/types';

// CSS class mappings for card styles
const borderRadiusClasses: Record<BorderRadius, string> = {
  none: '',
  sm: 'rounded',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  xl: 'rounded-2xl',
  '2xl': 'rounded-3xl',
};

const shadowClasses: Record<ShadowSize, string> = {
  none: '',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
};

const borderWidthClasses: Record<BorderWidth, string> = {
  none: '',
  thin: 'border',
  medium: 'border-2',
  thick: 'border-4',
};

const iconShapeClasses: Record<IconShape, string> = {
  square: 'overflow-hidden',
  rounded: 'rounded-lg overflow-hidden',
  circle: 'rounded-full overflow-hidden',
};

const iconSizeClasses: Record<IconSize, string> = {
  sm: 'w-12 h-12',
  md: 'w-16 h-16 @md:w-20 @md:h-20',
  lg: 'w-20 h-20 @md:w-24 @md:h-24',
};

export function getCardClasses(cardStyles?: CardStyles) {
  if (!cardStyles) {
    // Return default classes if no styles are specified
    return 'rounded-xl shadow-lg bg-white border border-gray-200';
  }

  const classes = [];

  // Border radius
  const radiusClass = borderRadiusClasses[cardStyles.borderRadius || 'lg'];
  if (radiusClass) {
    classes.push(radiusClass);
  }

  // Shadow
  const shadowClass = shadowClasses[cardStyles.shadow || 'lg'];
  if (shadowClass) {
    classes.push(shadowClass);
  }

  // Border
  const borderClass = borderWidthClasses[cardStyles.borderWidth || 'none'];
  if (borderClass) {
    classes.push(borderClass);
  }

  // Hover effects
  if (cardStyles.hoverLift && cardStyles.hoverLift !== 'none') {
    classes.push('transition-all duration-300 hover:shadow-xl');
    if (cardStyles.hoverLift === 'sm') {
      classes.push('hover:-translate-y-1');
    } else if (cardStyles.hoverLift === 'lg') {
      classes.push('hover:-translate-y-2');
    }
  }

  return classes.join(' ');
}

export function getCardStyle(cardStyles?: CardStyles) {
  if (!cardStyles) {
    return {};
  }

  const style: Record<string, string> = {};

  // Background
  if (cardStyles.backgroundType === 'gradient') {
    const from = cardStyles.gradientFrom || '#ffffff';
    const to = cardStyles.gradientTo || '#f3f4f6';
    style.background = `linear-gradient(to bottom right, ${from}, ${to})`;
  } else if (cardStyles.backgroundColor) {
    style.backgroundColor = cardStyles.backgroundColor;
  }

  // Border color
  if (cardStyles.borderColor && cardStyles.borderWidth !== 'none') {
    style.borderColor = cardStyles.borderColor;
  }

  return style;
}

export function getIconClasses(iconStyles?: IconStyles) {
  if (!iconStyles) {
    // Return default classes
    return 'w-16 h-16 @md:w-20 @md:h-20 rounded-xl';
  }

  const classes = [];

  // Size
  const sizeClass = iconSizeClasses[iconStyles.size || 'md'];
  classes.push(sizeClass);

  // Shape
  const shapeClass = iconShapeClasses[iconStyles.shape || 'rounded'];
  if (shapeClass) {
    classes.push(shapeClass);
  }

  // Shadow
  const shadowClass = shadowClasses[iconStyles.shadow || 'none'];
  if (shadowClass) {
    classes.push(shadowClass);
  }

  // Border
  const borderClass = borderWidthClasses[iconStyles.borderWidth || 'none'];
  if (borderClass) {
    classes.push(borderClass);
  }

  return classes.join(' ');
}

export function getIconStyle(iconStyles?: IconStyles, isBgLight = true) {
  if (!iconStyles) {
    // Return default gradient style
    if (isBgLight) {
      return { background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)' };
    } else {
      return {
        background:
          'linear-gradient(to bottom right, rgba(99, 102, 241, 0.2), rgba(79, 70, 229, 0.2))',
      };
    }
  }

  const style: Record<string, string> = {};

  // Background color
  if (iconStyles.backgroundColor) {
    style.backgroundColor = iconStyles.backgroundColor;
  }

  // Border color
  if (iconStyles.borderColor && iconStyles.borderWidth !== 'none') {
    style.borderColor = iconStyles.borderColor;
  }

  return style;
}
